/**
 * Suggest canonical WPS pass labels for numbered Fill / Cap / Strip names.
 * Used by TXT/Data360 pass mapping when global suggestions and identity match fail.
 */
export function suggestNumberedPassMapping(
  rawPassName: string,
  canonicalLabels: readonly string[]
): string | null {
  const base = String(rawPassName ?? '').trim()
  if (!base) return null

  const exact = canonicalLabels.find((l) => l.toLowerCase() === base.toLowerCase())
  if (exact) return exact

  const fillPatterns = [
    /^Fill\s*(\d+)$/i,
    /^F\s*(\d+)$/i,
    /^Fill(\d+)$/i,
    /^FILL\s*(\d+)$/i
  ]
  for (const pattern of fillPatterns) {
    const match = pattern.exec(base)
    if (match) {
      const target = `Fill ${match[1]}`
      const hit = canonicalLabels.find((l) => l.toLowerCase() === target.toLowerCase())
      if (hit) return hit
    }
  }

  if (/^Last\s+Fill$/i.test(base)) {
    const exactLastFill = canonicalLabels.find((l) => /^Last\s+Fill$/i.test(l))
    if (exactLastFill) return exactLastFill
    return 'Last Fill'
  }

  const capPatterns = [/^Cap\s*(\d+)$/i, /^CAP\s*(\d+)$/i, /^C\s*(\d+)$/i]
  for (const pattern of capPatterns) {
    const match = pattern.exec(base)
    if (match) {
      const target = `Cap ${match[1]}`
      const hit = canonicalLabels.find((l) => l.toLowerCase() === target.toLowerCase())
      if (hit) return hit
    }
  }

  const stripPatterns = [
    /^Strip\s*(\d+)?$/i,
    /^Strip\s+Pass$/i,
    /^S\s*(\d+)$/i,
    /^STRIP\s*(\d+)?$/i
  ]
  for (const pattern of stripPatterns) {
    if (pattern.test(base)) {
      const numbered = base.match(/(\d+)/)
      const target = numbered ? `Strip ${numbered[1]}` : 'Strip 1'
      const hit = canonicalLabels.find((l) => l.toLowerCase() === target.toLowerCase())
      if (hit) return hit
      const anyStrip = canonicalLabels.find((l) => /^Strip \d+$/i.test(l))
      if (anyStrip) return anyStrip
    }
  }

  if (/^Root$/i.test(base)) {
    const hit = canonicalLabels.find((l) => l.toLowerCase() === 'root')
    if (hit) return hit
  }
  if (/^Hot(\s+Pass)?(\s+\d+)?$/i.test(base)) {
    const hit = canonicalLabels.find((l) => l.toLowerCase() === 'hot')
    if (hit) return hit
  }

  return null
}
